import type { InstallationResolveLineItem } from "../types/installation";
import { readInstallationLineTaxable } from "../utils/readResolveTaxable";
import {
  getResolveRequestUnitPrice,
  orderLineKey,
} from "utils/orderResolveLinePricing";
import { round2 } from "utils/fixedAmountDiscountAllocation";

const EXCLUDED_SOURCE_ID = "000000000000000000000000";

export type InstallationEditResolveProduct = {
  product_id: string;
  variant_id?: string;
  quantity?: number;
  name?: string;
  sku?: string;
  unit_price?: number;
};

/**
 * Build resolve line_items for installation edit / work-order flows from saved order lines
 * and optional in-memory product selection (qty changes while editing).
 * Coupon discount_code is sent on payload root only — not on line_items.
 */
export function buildInstallationEditResolveLineItems(
  orderLineItems: unknown[] | null | undefined,
  selectedProducts?: InstallationEditResolveProduct[] | null,
  manualUnitPriceMap?: Record<string, number> | null
): InstallationResolveLineItem[] {
  const productsByKey = new Map(
    (selectedProducts || []).map((p) => [
      `${String(p.product_id)}:${String(p.variant_id ?? "")}`,
      p,
    ])
  );

  const rows: InstallationResolveLineItem[] = [];

  for (const raw of orderLineItems || []) {
    const li = raw as Record<string, unknown>;
    if (li?.is_new_purchase !== true) continue;

    const invSource = String(li?.inventory_source ?? "");
    const sourceId =
      li?.source_product_id != null ? String(li.source_product_id) : "";
    if (invSource === "rotational" || sourceId === EXCLUDED_SOURCE_ID || !sourceId) {
      continue;
    }

    const productId = String(li?.product_id ?? "");
    if (!productId) continue;

    const variantId = String(li?.variant_id ?? "");
    const key = orderLineKey(productId, variantId);
    const selected = productsByKey.get(`${productId}:${variantId}`);
    const qty = Math.max(1, Number(selected?.quantity ?? li?.quantity ?? 1));
    const baseUnit = round2(
      Number(selected?.unit_price ?? li?.unit_price ?? li?.changed_unit_price ?? 0)
    );
    const isManual = manualUnitPriceMap?.[key] !== undefined;
    const unitPrice = isManual
      ? round2(Number(manualUnitPriceMap![key]))
      : getResolveRequestUnitPrice(key, baseUnit, manualUnitPriceMap ?? {});
    if (unitPrice <= 0) continue;

    const sku = String(
      selected?.sku ??
        li?.sku ??
        (invSource === "custom" ? li?.custom_sku : "") ??
        ""
    ).trim();

    const isCustomLine = invSource === "custom";
    const priceOverridden = isManual || isCustomLine;

    rows.push({
      product_id: productId,
      variant_id: variantId,
      product_name: String(li?.product_name ?? selected?.name ?? ""),
      ...(sku ? { sku } : {}),
      inventory_source: invSource || "agent",
      is_new_purchase: true,
      quantity: qty,
      unit_price: unitPrice,
      ...(priceOverridden ? { unit_retail_price: unitPrice, changed_unit_price: unitPrice } : {}),
      is_price_overridden: priceOverridden,
      is_taxable: readInstallationLineTaxable(li, true),
      itemType: "default",
    });
  }

  return rows;
}
